import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  CaptchaConfigDTO,
  CaptchaConfigUpdateParams,
  CaptchaProvider,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid captcha config': '人机验证配置无效，请检查当前启用通道的必填项',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

type CaptchaForm = CaptchaConfigUpdateParams;

const EMPTY_FORM: CaptchaForm = {
  task_claim_provider: 'tencent',
  provider: 'disabled',
  aliyun: {
    access_key_id: '',
    access_key_secret: '',
    region: '',
    endpoint: '',
    scene_id: '',
    prefix: '',
  },
  tencent: {
    secret_id: '',
    secret_key: '',
    captcha_app_id: 0,
    app_secret_key: '',
    client_js_url: 'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js',
  },
};

function dtoToForm(dto: CaptchaConfigDTO): CaptchaForm {
  return {
    provider: dto.provider,
    task_claim_provider: dto.task_claim_provider ?? 'tencent',
    aliyun: {
      access_key_id: dto.aliyun.access_key_id,
      access_key_secret: dto.aliyun.access_key_secret,
      region: dto.aliyun.region,
      endpoint: dto.aliyun.endpoint,
      scene_id: dto.aliyun.scene_id,
      prefix: dto.aliyun.prefix,
    },
    tencent: {
      secret_id: dto.tencent.secret_id,
      secret_key: dto.tencent.secret_key,
      captcha_app_id: dto.tencent.captcha_app_id,
      app_secret_key: dto.tencent.app_secret_key,
      client_js_url: dto.tencent.client_js_url,
    },
  };
}

export function useCaptchaConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<CaptchaConfigDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingProvider, setDeletingProvider] = useState<'aliyun' | 'tencent' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CaptchaForm>(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getCaptchaConfig();
      setData(result);
      setForm(dtoToForm(result));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setProvider = useCallback((provider: CaptchaProvider) => {
    setForm((prev) => ({ ...prev, provider }));
  }, []);

  const updateAliyunField = useCallback(
    <K extends keyof CaptchaForm['aliyun']>(key: K, value: CaptchaForm['aliyun'][K]) => {
      setForm((prev) => ({ ...prev, aliyun: { ...prev.aliyun, [key]: value } }));
    },
    [],
  );

  const updateTencentField = useCallback(
    <K extends keyof CaptchaForm['tencent']>(key: K, value: CaptchaForm['tencent'][K]) => {
      setForm((prev) => ({ ...prev, tencent: { ...prev.tencent, [key]: value } }));
    },
    [],
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const result = await settingsService.updateCaptchaConfig(form);
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', '人机验证配置已保存，并将在下一次校验时立即生效');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [addToast, form]);

  const removeProvider = useCallback(async (provider: 'aliyun' | 'tencent') => {
    setDeletingProvider(provider);
    try {
      const result = await settingsService.deleteCaptchaProvider(provider);
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', `${provider === 'aliyun' ? '阿里云' : '腾讯云'}配置已删除`);
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setDeletingProvider(null);
    }
  }, [addToast]);

  return {
    data,
    form,
    loading,
    saving,
    deletingProvider,
    error,
    setProvider,
    setTaskClaimProvider: (task_claim_provider: CaptchaForm['task_claim_provider']) => setForm((prev) => ({ ...prev, task_claim_provider })),
    updateAliyunField,
    updateTencentField,
    save,
    removeProvider,
    reload: load,
  };
}
